import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { TierBadge } from '@/components/common/TierBadge';
import { StatusBadge } from '@/components/common/StatusBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Award,
  TrendingUp,
  Calendar,
  Star,
  FileText,
  Users,
  LogOut,
} from 'lucide-react';
import API from '@/api/client';
import { formatNumber, formatCurrency } from '@/utils/helpers';
import { logout } from '@/utils/storage';
import { toast } from 'sonner';

const TherapistDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [referralsData, setReferralsData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profile, performance, referrals] = await Promise.all([
        API.get('/therapist/me'),
        API.get('/therapist/performance'),
        API.get('/therapist/referrals'),
      ]);
      setProfileData(profile.data);
      setPerformanceData(performance.data);
      setReferralsData(referrals.data);
    } catch (error) {
      console.error('Error fetching therapist data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading dashboard..." />;
  }

  if (!profileData) {
    return <div className="text-center py-12">Failed to load data</div>;
  }

  const { metrics } = performanceData;
  const { summary } = referralsData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
              alt="Physiomobile"
              className="h-10"
            />
            <div>
              <h1 className="font-heading text-xl font-bold text-gray-800">
                Therapist Hub
              </h1>
              <p className="text-sm text-muted-foreground">
                {profileData.user?.full_name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <TierBadge tier={metrics.tier} size="lg" />
            <Button onClick={logout} variant="ghost" size="sm">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader
          title="Your Performance"
          subtitle="Track your progress and grow your practice"
          actions={
            <div className="flex gap-2">
              <Button onClick={() => navigate('/therapist/content')} variant="outline">
                <FileText className="w-4 h-4 mr-2" />
                My Content
              </Button>
              <Button onClick={() => navigate('/therapist/referral/create')}>
                <Users className="w-4 h-4 mr-2" />
                Refer & Earn
              </Button>
            </div>
          }
        />

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Sessions"
            value={formatNumber(metrics.total_sessions)}
            icon={Calendar}
            testId="therapist-total-sessions"
          />

          <MetricCard
            title="Acceptance Rate"
            value={`${(metrics.acceptance_rate * 100).toFixed(0)}%`}
            icon={TrendingUp}
            testId="therapist-acceptance-rate"
          />

          <MetricCard
            title="Average Rating"
            value={metrics.average_rating.toFixed(1)}
            subtitle="Out of 5.0"
            icon={Star}
            testId="therapist-avg-rating"
          />

          <MetricCard
            title="Total Earnings"
            value={formatCurrency(metrics.total_earnings)}
            icon={Award}
            testId="therapist-earnings"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tier Progress */}
          <Card className="p-6" data-testid="tier-progress-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Tier Progress
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Current Tier</span>
                <TierBadge tier={metrics.tier} />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sessions Completed</span>
                  <span className="font-medium">{metrics.total_sessions}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Acceptance Rate</span>
                  <span className="font-medium">
                    {(metrics.acceptance_rate * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Availability Hours</span>
                  <span className="font-medium">{metrics.availability_hours}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Patient Rating</span>
                  <span className="font-medium">{metrics.average_rating.toFixed(1)} / 5.0</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Cancellation Rate</span>
                  <span className="font-medium">
                    {(metrics.cancellation_rate * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800">
                  💡 <strong>Tip:</strong> Maintain high acceptance rates and availability to unlock higher tiers and bonuses!
                </p>
              </div>
            </div>
          </Card>

          {/* Referrals Summary */}
          <Card className="p-6" data-testid="referrals-summary-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Referral Rewards
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <p className="text-sm text-yellow-800 font-medium mb-1">Pending</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    {summary.pending}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium mb-1">Qualified</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {summary.qualified}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-sm text-green-800 font-medium mb-1">Paid Out</p>
                  <p className="text-2xl font-bold text-green-900">
                    {summary.paid}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-sm text-primary font-medium mb-1">Total Earned</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(summary.total_earnings)}
                  </p>
                </div>
              </div>
              <Button className="w-full" onClick={() => navigate('/therapist/referral/create')} data-testid="create-referral-button">
                <Users className="w-4 h-4 mr-2" />
                Refer a Patient or Therapist
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Earn RM35-RM100 for each successful referral
              </p>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default TherapistDashboard;